import React, { useState, useEffect } from 'react';
import {
  ResourceList,
  ResourceItem,
  Stack,
  ButtonGroup,
  Button,
  TextStyle,
  Icon,
  Badge,
  Tooltip,
  Text
} from '@shopify/polaris';
import './TemplateList.css'; // Import custom CSS for styling overrides
import { DeleteMajor, EditMajor, DuplicateMinor, PlayMajor, DragHandleMinor } from '@shopify/polaris-icons';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

/**
 * A component for displaying and managing a list of templates
 * 
 * @param {Object} props
 * @param {Array} props.templates - Array of template objects to display
 * @param {Array} props.selectedTemplates - Array of selected template IDs
 * @param {Function} props.handleTemplateSelect - Callback when a template is selected
 * @param {Function} props.handleDelete - Callback when delete button is clicked
 * @param {Function} props.handleEdit - Callback when edit button is clicked
 * @param {Function} props.handleDuplicate - Callback when duplicate button is clicked
 * @param {Function} props.handleExecute - Callback when execute button is clicked
 * @param {Function} props.onReorder - Callback when templates are reordered
 * @param {Array} props.aiModels - Array of available AI models
 */
export const TemplateList = ({
  templates = [],
  selectedTemplates = [],
  handleTemplateSelect,
  handleDelete,
  handleEdit,
  handleDuplicate,
  handleExecute,
  onReorder,
  aiModels = [] // New prop for available AI models
}) => {
  // Helper function to get model display name
  const getModelDisplayName = (modelId) => {
    if (!modelId || !aiModels || aiModels.length === 0) {
      // Find the default model if no specific model is selected
      const defaultModel = aiModels?.find(m => m.isDefault);
      return defaultModel ? defaultModel.displayName : 'Unknown';
    }
    
    const model = aiModels.find(m => m.id === modelId);
    if (!model) return 'Unknown';
    
    // Add '(Vision)' suffix for vision-capable models
    return model.type === 'vision' ? `${model.displayName} (V)` : model.displayName;
  };

  // Helper function to get model cost per output field
  const getModelCostPerOutput = (modelId) => {
    if (!modelId || !aiModels || aiModels.length === 0) return 1;
    const model = aiModels.find(m => m.id === modelId);
    
    return model ? model.creditCostPerOutput || 1 : 1;
  };

  // Helper function to calculate total credits
  const calculateTotalCredits = (template, modelId) => {
    // For tool templates, always return 0 credits
    if (template.type === 'tool') {
      return 0;
    }
    
    const costPerOutput = getModelCostPerOutput(modelId);
    
    // If the template has a predefined creditCost, use that
    if (template.creditCost) {
      return template.creditCost;
    }
    
    // Handle sidebar-only templates (especially for image templates)
    if (template.sidebarOnly === true) {
      // For sidebar-only templates, count as 1 output × model's cost per output
      return 1 * costPerOutput;
    }
    
    // Count both output fields and output metafields
    const outputFieldsCount = (template.outputFields || []).length;
    const outputMetafieldsCount = (template.outputMetafields || []).length;
    const totalOutputs = outputFieldsCount + outputMetafieldsCount;
    
    // Calculate total credits
    const total = totalOutputs * costPerOutput;
    
    // Always return at least 1 credit if there are no outputs but the template is not marked as sidebarOnly
    return Math.max(total, 1);
  };

  // Internal state to maintain visual stability during drag operations
  const [internalItems, setInternalItems] = useState(templates);
  
  // Update internal items when external templates change
  useEffect(() => {
    setInternalItems(templates);
  }, [templates]);
  
  // Simple styles for dragging - minimal to let react-beautiful-dnd's defaults work
  const getItemStyle = (isDragging, draggableStyle) => ({
    // Apply a subtle highlight when dragging
    backgroundColor: isDragging ? '#f9fafb' : undefined,
    // This is important - we must pass on the draggableStyle
    ...draggableStyle
  });
  
  const getListStyle = isDraggingOver => ({
    // Subtle background change when dragging over the list
    backgroundColor: isDraggingOver ? '#f4f6f8' : undefined,
    padding: '4px',
    width: '100%'
  });

  // Handle drag end event according to react-beautiful-dnd documentation
  // Handle drag end event from react-beautiful-dnd
  const handleDragEnd = (result) => {
    console.log('Drag end result:', result);
    
    // Don't continue if dropped outside the list
    if (!result.destination) {
      console.log('Dropped outside the list');
      return;
    }
    
    // Extract the info we need from the result
    const { draggableId, source, destination } = result;
    
    console.log('Source:', source);
    console.log('Destination:', destination);
    console.log('DraggableId:', draggableId);
    
    // Find the template we're dragging based on its ID
    const templateIndex = internalItems.findIndex(t => t.id === draggableId);
    if (templateIndex === -1) {
      console.error('Could not find dragged template with ID:', draggableId);
      return;
    }
    
    // Get the template object
    const draggedTemplate = internalItems[templateIndex];
    console.log('Dragged template:', draggedTemplate.name, 'with ID:', draggedTemplate.id);
    
    // Determine destination index - this is tricky due to the unexpected behavior
    let destinationIndex;
    
    // Check if source.index and destination.index are numbers or strings
    const sourceIsNumeric = !isNaN(parseInt(source.index, 10));
    const destIsNumeric = !isNaN(parseInt(destination.index, 10));
    
    if (sourceIsNumeric && destIsNumeric) {
      // Normal case: indices are numbers
      destinationIndex = parseInt(destination.index, 10);
      console.log('Using numeric destination index:', destinationIndex);
    } else {
      // Unusual case: indices are template IDs
      // Find the index of the destination template ID in our array
      const destTemplateIndex = internalItems.findIndex(t => t.id === destination.index);
      if (destTemplateIndex !== -1) {
        destinationIndex = destTemplateIndex;
        console.log('Found destination index from template ID:', destinationIndex);
      } else {
        // Fallback: try to use the source index as a numeric index
        destinationIndex = parseInt(destination.index, 10);
        if (isNaN(destinationIndex)) {
          console.error('Could not determine destination index');
          return;
        }
        console.log('Using parsed destination index:', destinationIndex);
      }
    }
    
    // Create a new array for reordering
    const newItems = [...internalItems];
    
    // Remove the dragged template from its current position
    newItems.splice(templateIndex, 1);
    
    // Make sure destination index is valid after removal
    destinationIndex = Math.min(destinationIndex, newItems.length);
    
    // Insert at the new position
    newItems.splice(destinationIndex, 0, draggedTemplate);
    
    // Update internal state for immediate UI update
    setInternalItems(newItems);
    
    // Calculate the destination template ID for parent component
    let destTemplateId = null;
    
    // Determine the destination template ID based on position
    if (destinationIndex === 0) {
      // Moving to the beginning - use the ID of the first item
      destTemplateId = newItems[0].id;
    } else if (destinationIndex >= newItems.length - 1) {
      // Moving to the end - use null to indicate append
      destTemplateId = null;
    } else {
      // Moving to the middle - use the ID of the template that comes AFTER
      destTemplateId = newItems[destinationIndex + 1].id;
    }
    
    // Call parent's reorder handler with template IDs
    if (onReorder) {
      console.log(`Reordering: sourceId=${draggableId}, destId=${destTemplateId}`);
      onReorder(draggableId, destTemplateId);
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="template-list">
        {(provided, snapshot) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            style={getListStyle(snapshot.isDraggingOver)}
            className="template-list"
          >
            {internalItems.map((template, index) => {
              const { id, name, type = 'template', modelId, outputFields = [], outputMetafields = [] } = template;
              const isSelected = selectedTemplates.includes(id);
              const selectionIndex = selectedTemplates.indexOf(id);
              
              return (
                <Draggable key={id} draggableId={id} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...getItemStyle(
                          snapshot.isDragging,
                          provided.draggableProps.style
                        ),
                        padding: '4px',
                        width: '100%',
                        ...provided.draggableProps.style
                      }}
                      className={`template-list-item ${snapshot.isDragging ? 'dragging' : ''}`}
                    >
                      <ResourceItem
                        id={id}
                        onClick={() => handleTemplateSelect && handleTemplateSelect(id)}
                        selected={isSelected}
                        accessibilityLabel={`Select ${name}`}
                        className="clean-resource-item" // Add custom class for styling
                        media={<span style={{ display: 'none' }}></span>} // Override media space to hide bullet
                      >
                        <Stack alignment="center">
                          <Stack.Item>
                            <div {...provided.dragHandleProps} style={{ cursor: 'grab', padding: '0 4px', marginRight: '4px' }}>
                              <Icon source={DragHandleMinor} color="subdued" />
                            </div>
                          </Stack.Item>
                          <Stack.Item>
                            {isSelected && (
                              <Badge status="success">{selectionIndex + 1}</Badge>
                            )}
                          </Stack.Item>
                          <Stack.Item fill>
                            <TextStyle variation={isSelected ? 'strong' : undefined}>
                              {name || 'Unnamed Template'}
                            </TextStyle>
                            <div>
                              <Stack spacing="tight">
                                <TextStyle variation="subdued">
                                  Type: {
                                    type === 'product' ? 'Product' : 
                                    type === 'variant' ? 'Variant' : 
                                    type === 'image' ? 'Image' : 
                                    type === 'tool' ? 'Tool Execution' : 
                                    'Unknown'
                                  }
                                </TextStyle>
                                {type !== 'tool' && (
                                  <>
                                    <TextStyle variation="subdued" style={{margin: '0 4px'}}>
                                      |
                                    </TextStyle>
                                    <TextStyle variation="subdued">
                                      Model: {getModelDisplayName(modelId)}
                                    </TextStyle>
                                  </>
                                )}
                                <TextStyle variation="subdued" style={{margin: '0 4px'}}>
                                  |
                                </TextStyle>
                                {type === 'tool' ? (
                                  <Badge status="success">No AI Credits</Badge>
                                ) : (
                                  <Tooltip content={`${outputFields.length + outputMetafields.length} output fields × ${getModelCostPerOutput(modelId)} credits per field`}>
                                    <Badge status="info">Credits: {calculateTotalCredits(template, modelId)}</Badge>
                                  </Tooltip>
                                )}
                              </Stack>
                            </div>
                          </Stack.Item>
                          {(handleExecute || handleEdit || handleDuplicate || handleDelete) && (
                            <Stack.Item>
                              <ButtonGroup segmented>
                                {handleExecute && (
                                  <Button 
                                    icon={<Icon source={PlayMajor} color="success" />} 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleExecute(id);
                                    }}
                                    accessibilityLabel={`Execute ${name}`}
                                  />
                                )}
                                {handleEdit && (
                                  <Button 
                                    icon={<Icon source={EditMajor} color="base" />} 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEdit(template);
                                    }}
                                    accessibilityLabel={`Edit ${name}`}
                                  />
                                )}
                                {handleDuplicate && (
                                  <Button 
                                    icon={<Icon source={DuplicateMinor} color="base" />} 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDuplicate(template);
                                    }}
                                    accessibilityLabel={`Duplicate ${name}`}
                                  />
                                )}
                                {handleDelete && (
                                  <Button 
                                    icon={<Icon source={DeleteMajor} color="critical" />} 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(template.id);
                                    }}
                                    accessibilityLabel={`Delete ${name}`}
                                    destructive
                                  />
                                )}
                              </ButtonGroup>
                            </Stack.Item>
                          )}
                        </Stack>
                      </ResourceItem>
                    </div>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default TemplateList;
